import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';

interface SiteHeaderProps {
  transparentOnTop?: boolean;
}

const navItems = [
  { to: '/', label: '首页', end: true },
  { to: '/topics', label: '吃瓜热榜', end: false },
  { to: '/activities', label: '互动玩法', end: false },
  { to: '/about', label: '关于我们', end: false },
  { to: '/faq', label: '常见问题', end: false },
];

export default function SiteHeader({ transparentOnTop = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const solid = !transparentOnTop || scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-[68px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" aria-label="瓜田福利社 首页">
          <span
            className={`w-9 h-9 flex items-center justify-center rounded-full text-lg transition-colors ${
              solid ? 'bg-primary-500 text-background-50' : 'bg-background-50 text-primary-600'
            }`}
          >
            <i className="ri-emotion-happy-line"></i>
          </span>
          <span
            className={`font-heading text-xl md:text-2xl tracking-wide transition-colors ${
              solid ? 'text-foreground-950' : 'text-background-50'
            }`}
          >
            瓜田福利社
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  isActive
                    ? solid
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-background-50/25 text-background-50'
                    : solid
                      ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/15'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/activities"
            className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            立即参与互动
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full text-xl ${
            solid ? 'text-foreground-900 bg-background-100' : 'text-background-50 bg-background-50/20'
          }`}
          aria-label={menuOpen ? '关闭菜单' : '打开菜单'}
          aria-expanded={menuOpen}
        >
          <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-sm ${
                    isActive ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/activities"
              onClick={() => setMenuOpen(false)}
              className="mt-2 px-4 py-3 rounded-lg bg-primary-500 text-background-50 text-sm text-center font-medium whitespace-nowrap"
            >
              立即参与互动
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}